/**
 * PaymentModal — liquidação (total) ou baixa parcial de um título de dívida.
 *
 * O tipo de liquidação NÃO é escolhido aqui: vem do botão que abriu o modal
 * (`mode`), então "Liquidar" e "Baixa Parcial" usam a mesma tela com rótulos e
 * settlement_type diferentes.
 *
 * Tipo de pagamento / fonte / sub-rogação / aval a devolver e o bloco do pagador
 * são compartilhados com o Registrar Pagamento do billing
 * (components/molecules/payment).
 *
 * TODO: backend de upload pendente (comprovante). O upload real já existe no billing
 * (POST /payments/{payment_id}/receipt), mas NÃO serve aqui: esta tela cria pagamento no
 * SETTLEMENT, cujo payload não aceita payment_id — e o id entra no nome canônico do arquivo
 * (yyyyMMdd_pagador_idcurto.pdf). Sem ele o arquivo nasceria fora da convenção e o download do
 * billing, que lê billing.payment.file_name_receipt, não o acharia. Para ligar: o settlement
 * precisa aceitar o payment_id do cliente e gravar file_name_receipt.
 */
import { useContext, useMemo, useState } from 'react';
import { FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import { registerPayment } from '../services/settlementApi';
import { SettlementType, PaymentType, PaymentMethod } from '../../../../constants/payment';
import PaymentTypeFields, { PaymentTypeValue, emptyPaymentTypeValue, assetDationTotal } from '../../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer, onlyDigits } from '../../../molecules/payment/PayerFields';
import PaymentDateInput, { toIsoInstant } from '../../../molecules/payment/PaymentDateInput';
import { dueDateHint, amountHint } from '../../../molecules/payment/paymentHints';
import HintBadge from '../../../molecules/payment/HintBadge';
import { toast } from 'react-toastify';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { AuthContext } from '../../../../services/Auth/AuthContext';

export type PaymentMode = 'TOTAL' | 'PARCIAL';

interface Props {
    titleDebt: any;
    /** definido pelo botão de origem — não há select de tipo de liquidação */
    mode: PaymentMode;
    onClose: () => void;
    onSuccess: () => void;
}

const MODE_LABELS: Record<PaymentMode, { title: string; confirm: string; pending: string }> = {
    TOTAL: { title: 'Liquidar', confirm: 'Liquidar', pending: 'Liquidando...' },
    PARCIAL: { title: 'Baixa Parcial', confirm: 'Baixar Parcial', pending: 'Baixando...' },
};

const LABEL = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const INPUT = 'w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed';

const PaymentModal: React.FC<Props> = ({ titleDebt, mode, onClose, onSuccess }) => {
    const labels = MODE_LABELS[mode];
    // esperado = o que falta no título; base das dicas de desconto/acréscimo
    const expected = parseFloat(titleDebt.amount) - parseFloat(titleDebt.paid_amount || '0');
    const auth = useContext(AuthContext);

    const [amount, setAmount] = useState(expected.toFixed(2));
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [time, setTime] = useState('00:00');
    const [showTime, setShowTime] = useState(false);
    const [notes, setNotes] = useState('');
    const [receiptName, setReceiptName] = useState('');
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(emptyPaymentTypeValue());
    // pagador do título: cadastro completo em `payer` (endereço + contato + external_id);
    // os campos soltos são o fallback de compat.
    const [payer, setPayer] = useState<PayerValue>(() => {
        const p = titleDebt.payer || {};
        const document = p.document_number || titleDebt.payer_document || '';
        return emptyPayer({
            document,
            // pagador que veio do título é dado de cadastro: entra travado (só o documento
            // fica editável). Trocar o documento reabre os campos — ver PayerFields.
            lookup: document ? 'found' : 'idle',
            name: p.name || titleDebt.payer_name || '',
            personId: p.external_id || null,
            email: p.email || '',
            phone: p.phone || '',
            address_street: p.address_street || '',
            address_number: p.address_number || '',
            address_complement: p.address_complement || '',
            address_neighborhood: p.address_neighborhood || '',
            address_city: p.address_city || '',
            address_state: p.address_state || '',
            address_zip_code: p.address_zip_code || '',
        });
    });
    const [submitting, setSubmitting] = useState(false);

    const dateHint = useMemo(() => dueDateHint(date, titleDebt.due_date), [date, titleDebt.due_date]);
    // desconto/acréscimo só faz sentido quando o pagamento quita o título: numa baixa
    // parcial o valor é naturalmente menor que o esperado, sem ser desconto.
    const valueHint = useMemo(
        () => (mode === 'TOTAL' ? amountHint(parseFloat(amount), expected) : null),
        [mode, amount, expected]);

    const handleSubmit = async () => {
        const parsedAmount = parseFloat(amount);
        if (!parsedAmount || parsedAmount <= 0) {
            toast.error('Informe um valor maior que zero');
            return;
        }
        if (typeFields.payment_type === PaymentType.ASSET_DATION
            && !(typeFields.asset_dation?.estimated_value)) {
            toast.error('Informe o valor estimado do bem dado em pagamento');
            return;
        }
        const isFinancial = typeFields.payment_type === PaymentType.FINANCIAL;
        setSubmitting(true);
        try {
            await registerPayment(titleDebt.title_debt_id ?? titleDebt.id, {
                amount: parsedAmount,
                payment_date: toIsoInstant(date, time, showTime),
                payment_type: typeFields.payment_type,
                payment_method: isFinancial ? typeFields.payment_method : undefined,
                settlement_type: mode === 'TOTAL' ? SettlementType.TOTAL : SettlementType.PARCIAL,
                bank_account: isFinancial ? typeFields.receiving_bank_account : undefined,
                receiving_bank_account_id: isFinancial ? typeFields.receiving_bank_account : undefined,
                payment_source: typeFields.payment_source,
                pending_guarantor_refund: typeFields.pending_guarantor_refund,
                notes,
                debt_confession_contract: typeFields.payment_type === PaymentType.DEBT_CONFESSION
                    ? typeFields.debt_confession_contract : undefined,
                asset_dation: typeFields.payment_type === PaymentType.ASSET_DATION
                    ? typeFields.asset_dation : undefined,
                // pagador editado na tela sobrepõe o payer do título (o backend usa este quando vem)
                payer: onlyDigits(payer.document) ? {
                    external_id: payer.personId || undefined,
                    name: payer.name || payer.document,
                    document_number: onlyDigits(payer.document),
                    email: payer.email || undefined,
                    phone: payer.phone || undefined,
                    address_street: payer.address_street || undefined,
                    address_number: payer.address_number || undefined,
                    address_complement: payer.address_complement || undefined,
                    address_neighborhood: payer.address_neighborhood || undefined,
                    address_city: payer.address_city || undefined,
                    address_state: payer.address_state || undefined,
                    address_zip_code: payer.address_zip_code || undefined,
                } : undefined,
                file_name_receipt: receiptName || undefined,
                user_responsible: auth?.user?.username || auth?.user?.email || undefined,
            });
            toast.success(mode === 'TOTAL' ? 'Título liquidado com sucesso' : 'Baixa parcial registrada com sucesso');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Erro ao registrar: ${e.message}`);
        }
        setSubmitting(false);
    };

    const assetTotal = assetDationTotal(typeFields.asset_dation);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{labels.title}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                {/* Context */}
                <div className="mb-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div className="grid grid-cols-2 gap-2">
                        <p><span className="text-gray-500">Contrato:</span> <strong>{titleDebt.contract_number}</strong></p>
                        <p><span className="text-gray-500">Parcela:</span> <strong>{titleDebt.document_title || titleDebt.installment_number || '-'}</strong></p>
                        <p><span className="text-gray-500">Valor:</span> <strong>{formatBRL(titleDebt.amount)}</strong></p>
                        <p><span className="text-gray-500">Vencimento:</span> <strong>{formatDateBR(titleDebt.due_date)}</strong></p>
                        <p><span className="text-gray-500">Já pago:</span> <strong>{formatBRL(titleDebt.paid_amount || '0')}</strong></p>
                        <p><span className="text-gray-500">Saldo:</span> <strong className="text-red-600">{formatBRL(expected)}</strong></p>
                    </div>
                </div>

                <div className="space-y-3">
                    {/* Data (horário opcional) + Valor, com as dicas comparativas */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={LABEL}>Data do Pagamento</label>
                            <PaymentDateInput date={date} time={time} showTime={showTime} className={INPUT}
                                onChange={next => { setDate(next.date); setTime(next.time); setShowTime(next.showTime); }} />
                            <HintBadge hint={dateHint} />
                        </div>
                        <div>
                            <label className={LABEL}>Valor do Pagamento (R$)</label>
                            <input type="number" step="0.01" className={INPUT}
                                value={amount} onChange={e => setAmount(e.target.value)} />
                            <HintBadge hint={valueHint} />
                        </div>
                    </div>

                    <PaymentTypeFields
                        value={typeFields}
                        onChange={next => {
                            // dação: o valor acompanha o total dos bens
                            const total = assetDationTotal(next.asset_dation);
                            if (next.payment_type === PaymentType.ASSET_DATION
                                && total !== assetDationTotal(typeFields.asset_dation)) {
                                setAmount(total.toFixed(2));
                            }
                            setTypeFields(next);
                        }}
                        methods={[PaymentMethod.PIX, PaymentMethod.TED, PaymentMethod.BOLETO]}
                        labelClassName={LABEL}
                        inputClassName={INPUT}
                        idPrefix="pay-modal"
                    />

                    {/* Pagador — carregado do título e editável */}
                    <div className="border-t dark:border-slate-700 pt-3">
                        <PayerFields value={payer} onChange={setPayer}
                            labelClassName={LABEL} inputClassName={INPUT} />
                    </div>

                    <div>
                        <label className={LABEL}>Comprovante de pagamento (opcional)
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">TODO: backend de upload pendente</span>
                        </label>
                        <label className="flex items-center gap-2 border border-dashed dark:border-slate-600 rounded px-3 py-2 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
                            <FaCloudUploadAlt /> {receiptName || 'Selecionar arquivo (não enviado ainda)'}
                            <input type="file" accept="application/pdf" className="hidden"
                                onChange={e => setReceiptName(e.target.files?.[0]?.name || '')} />
                        </label>
                    </div>

                    <div>
                        <label className={LABEL}>Observações</label>
                        <input type="text" className={INPUT}
                            value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="Observações opcionais..." />
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-5 flex justify-between items-center">
                    {typeFields.payment_type === PaymentType.ASSET_DATION && assetTotal > 0 && (
                        <span className="text-sm text-green-600 font-medium">Total bens: {formatBRL(assetTotal)}</span>
                    )}
                    <div className="flex gap-2 ml-auto">
                        <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-gray-300">
                            Cancelar
                        </button>
                        <button onClick={handleSubmit} disabled={submitting}
                            className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                            {submitting ? labels.pending : labels.confirm}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
